import { useState, useEffect, useCallback } from 'react';
import { Connection, PublicKey, SystemProgram, Transaction, LAMPORTS_PER_SOL } from '@solana/web3.js';
import type { VersionedTransaction } from '@solana/web3.js';
import {
  getAssociatedTokenAddress,
  createAssociatedTokenAccountInstruction,
  createTransferInstruction,
  getAccount,
} from '@solana/spl-token';
import { confirmSignatureHttp, isTransactionExpiredTimeoutError } from '@/lib/solana/confirmSignatureHttp';
import { addBreadcrumb } from '@/lib/sentry';
import { getSolanaEndpoint } from '@/lib/solana/config';
import { debug } from '@/lib/debug';
import { toast } from 'sonner';
import type { WalletToken } from '@/hooks/useTokenBalances';

const MAX_RETRIES = 3;

// Reserve for the network fee plus headroom for priority fees and retries with fresh
// blockhash. Real fee is ~5,000 lamports; 1,000,000 lamports (0.001 SOL) keeps a comfortable
// buffer without surprising the user. Max-button math and pre-flight validation share this
// constant so they cannot disagree.
const SOL_FEE_RESERVE_LAMPORTS = 1_000_000;

function formatLamportsAsSol(lamports: number): string {
  if (lamports <= 0) return '0';
  const whole = Math.floor(lamports / LAMPORTS_PER_SOL);
  const frac = lamports - whole * LAMPORTS_PER_SOL;
  if (frac === 0) return whole.toString();
  const fracStr = frac.toString().padStart(9, '0').replace(/0+$/, '');
  return `${whole}.${fracStr}`;
}

export type WithdrawStatus = 'idle' | 'signing' | 'broadcasting' | 'confirming' | 'success' | 'error' | 'pending';

interface UseWithdrawOptions {
  publicKey: PublicKey | null;
  sendTransaction: ((tx: Transaction | VersionedTransaction, connection?: unknown) => Promise<string>) | undefined;
  balance: number | null;
  selectedToken: WalletToken | null;
  refetchBalance: () => void;
  refetchTokens: () => void;
  onPreSign?: () => Promise<void>;
  onWithdrawConfirmed?: (address: string) => void;
}

export function useWithdraw({
  publicKey,
  sendTransaction,
  balance,
  selectedToken,
  refetchBalance,
  refetchTokens,
  onPreSign,
  onWithdrawConfirmed,
}: UseWithdrawOptions) {
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawStatus, setWithdrawStatus] = useState<WithdrawStatus>('idle');
  const [pendingSignature, setPendingSignature] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [needsAtaCreation, setNeedsAtaCreation] = useState(false);

  const isWithdrawing = withdrawStatus !== 'idle' && withdrawStatus !== 'success' && withdrawStatus !== 'error';
  const withdrawSymbol = selectedToken?.symbol ?? 'SOL';

  // Proactive ATA existence check for SPL token withdrawals
  useEffect(() => {
    if (!selectedToken || !withdrawAddress) {
      setNeedsAtaCreation(false);
      return;
    }

    let recipientPubkey: PublicKey;
    try {
      recipientPubkey = new PublicKey(withdrawAddress);
    } catch {
      setNeedsAtaCreation(false);
      return;
    }

    let cancelled = false;
    const mintPubkey = new PublicKey(selectedToken.mint);

    (async () => {
      try {
        const connection = new Connection(getSolanaEndpoint(), 'confirmed');
        const tokenProgramId = new PublicKey(selectedToken.programId);
        const recipientAta = await getAssociatedTokenAddress(mintPubkey, recipientPubkey, false, tokenProgramId);
        await getAccount(connection, recipientAta, undefined, tokenProgramId);
        if (!cancelled) setNeedsAtaCreation(false);
      } catch {
        // Account doesn't exist -- ATA will need to be created
        if (!cancelled) setNeedsAtaCreation(true);
      }
    })();

    return () => { cancelled = true; };
  }, [selectedToken, withdrawAddress]);

  const getSolscanUrl = (signature: string) => {
    const isDevnet = import.meta.env.VITE_USE_DEVNET === 'true';
    const cluster = isDevnet ? '?cluster=devnet' : '';
    return `https://solscan.io/tx/${signature}${cluster}`;
  };

  const handleMaxAmount = useCallback(() => {
    if (selectedToken !== null) {
      setWithdrawAmount(selectedToken.uiAmount.toString());
    } else if (balance !== null) {
      const balanceLamports = Math.round(balance * LAMPORTS_PER_SOL);
      const maxLamports = Math.max(0, balanceLamports - SOL_FEE_RESERVE_LAMPORTS);
      setWithdrawAmount(formatLamportsAsSol(maxLamports));
    }
  }, [selectedToken, balance]);

  const handleWithdraw = useCallback(async () => {
    if (!publicKey || !sendTransaction) {
      toast.error('Wallet not connected');
      return;
    }

    if (!withdrawAddress || !withdrawAmount) {
      toast.error('Please enter destination address and amount');
      return;
    }

    let destinationPubkey: PublicKey;
    try {
      destinationPubkey = new PublicKey(withdrawAddress);
    } catch {
      toast.error('Invalid destination address');
      return;
    }

    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Invalid amount');
      return;
    }

    if (selectedToken !== null) {
      if (amount > selectedToken.uiAmount) {
        toast.error(`Insufficient ${selectedToken.symbol} balance`);
        return;
      }
      if (balance !== null && balance < 0.005) {
        toast.error('Insufficient SOL for transaction fees');
        return;
      }
    } else {
      if (balance !== null) {
        const balanceLamports = Math.round(balance * LAMPORTS_PER_SOL);
        const amountLamports = Math.round(amount * LAMPORTS_PER_SOL);
        if (amountLamports > balanceLamports - SOL_FEE_RESERVE_LAMPORTS) {
          toast.error('Insufficient balance (need to reserve for fees)');
          return;
        }
      }
    }

    setPendingSignature(null);
    setRetryCount(0);
    addBreadcrumb('Withdrawal initiated', 'transaction', { amount, destination: withdrawAddress.slice(0, 8) });

    // Store form values before any pre-sign action
    const txAmount = amount;
    const txDestination = destinationPubkey;
    const txToken = selectedToken;

    // Call pre-sign callback (WalletModal uses this to close modal before Turnkey signing)
    if (onPreSign) {
      await onPreSign();
    }

    setWithdrawStatus('signing');

    // Use centralized Solana config for direct Helius connection
    const rpcEndpoint = getSolanaEndpoint();

    const connection = new Connection(rpcEndpoint, {
      commitment: 'confirmed',
    });

    let attempts = 0;
    let lastError: Error | null = null;

    while (attempts < MAX_RETRIES) {
      attempts++;

      try {
        const transaction = new Transaction();

        if (txToken !== null) {
          // SPL token transfer (supports both TOKEN_PROGRAM_ID and TOKEN_2022_PROGRAM_ID)
          const mintPubkey = new PublicKey(txToken.mint);
          const tokenProgramId = new PublicKey(txToken.programId);
          const senderAta = await getAssociatedTokenAddress(mintPubkey, publicKey, false, tokenProgramId);
          const recipientAta = await getAssociatedTokenAddress(mintPubkey, txDestination, false, tokenProgramId);
          const rawAmount = BigInt(Math.floor(txAmount * Math.pow(10, txToken.decimals)));

          // Check if recipient ATA exists; create if needed
          try {
            await getAccount(connection, recipientAta, undefined, tokenProgramId);
          } catch {
            transaction.add(
              createAssociatedTokenAccountInstruction(publicKey, recipientAta, txDestination, mintPubkey, tokenProgramId)
            );
          }

          transaction.add(
            createTransferInstruction(senderAta, recipientAta, publicKey, rawAmount, [], tokenProgramId)
          );
        } else {
          // SOL transfer
          const lamports = Math.floor(txAmount * LAMPORTS_PER_SOL);
          transaction.add(
            SystemProgram.transfer({
              fromPubkey: publicKey,
              toPubkey: txDestination,
              lamports,
            })
          );
        }

        // Fetch FRESH blockhash for each attempt to maximize validity window
        const { blockhash } = await connection.getLatestBlockhash('finalized');
        transaction.recentBlockhash = blockhash;
        transaction.feePayer = publicKey;

        if (attempts > 1) {
          setRetryCount(attempts);
          addBreadcrumb('Withdrawal retry with fresh blockhash', 'transaction', { attempt: attempts });
          toast.info(`Retrying transaction (${attempts}/${MAX_RETRIES})...`);
        }

        addBreadcrumb('Withdrawal signing started', 'transaction', { attempt: attempts });

        // Sign and send transaction
        const signature = await sendTransaction(transaction, connection);

        setWithdrawStatus('broadcasting');
        setPendingSignature(signature);
        addBreadcrumb('Withdrawal broadcasted', 'transaction', { signature: signature.slice(0, 16), attempt: attempts });

        // Immediately refetch balance (optimistic)
        refetchBalance();

        setWithdrawStatus('confirming');

        // Confirm via HTTP polling only (no WebSocket, which fails in browser)
        let confirmed = false;

        const httpResult = await confirmSignatureHttp(connection, signature, {
          commitment: 'confirmed',
          timeoutMs: 30000,
          pollIntervalMs: 2000,
        });

        if (httpResult.confirmed) {
          confirmed = true;
        } else if (httpResult.pending) {
          // Transaction still pending - show pending state.
          // Persistent notification is written by the helius-webhook when the
          // outgoing transfer is observed on-chain; no client-side write here.
          setWithdrawStatus('pending');

          toast.info('Transaction submitted but confirmation pending', {
            description: 'Check Solscan for status',
            action: {
              label: 'View',
              onClick: () => window.open(getSolscanUrl(signature), '_blank'),
            },
          });
          setRetryCount(0);
          return;
        } else if (httpResult.error) {
          throw new Error(httpResult.error);
        }

        if (confirmed) {
          setWithdrawStatus('success');
          addBreadcrumb('Withdrawal confirmed', 'transaction', { signature: signature.slice(0, 16), amount, attempts });

          // Persistent notification is written by the helius-webhook when the
          // outgoing transfer is observed on-chain; no client-side write here.

          toast.success(`Withdrawal successful! ${amount} ${txToken?.symbol ?? 'SOL'} sent`, {
            action: {
              label: 'View',
              onClick: () => window.open(getSolscanUrl(signature), '_blank'),
            },
          });

          setWithdrawAddress('');
          setWithdrawAmount('');
          setPendingSignature(null);
          setRetryCount(0);
          refetchBalance();
          refetchTokens();

          // Record address for history dropdown (fire and forget)
          onWithdrawConfirmed?.(txDestination.toBase58());

          // Reset to idle after a short delay
          setTimeout(() => setWithdrawStatus('idle'), 2000);
          return; // Success - exit the retry loop
        }
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown error');

        // Check if this is a recoverable blockhash expiry error
        if (isTransactionExpiredTimeoutError(lastError) && attempts < MAX_RETRIES) {
          addBreadcrumb('Blockhash expired, will retry', 'transaction', {
            attempt: attempts,
            error: lastError.message
          });
          debug.trace('[useWithdraw] Blockhash expired, retrying with fresh blockhash...');
          setWithdrawStatus('signing'); // Reset to signing for retry
          continue; // Retry with fresh blockhash
        }

        // Non-recoverable error or max retries reached - break out and handle error
        break;
      }
    }

    // If we get here, all retries failed
    console.error('[useWithdraw] Withdraw error after retries:', lastError);
    setWithdrawStatus('error');
    setRetryCount(0);
    addBreadcrumb('Withdrawal failed after retries', 'transaction', {
      error: lastError?.message || 'Unknown error',
      attempts
    });
    toast.error(lastError?.message || 'Withdrawal failed');

    // Reset to idle after showing error
    setTimeout(() => setWithdrawStatus('idle'), 2000);
  }, [publicKey, sendTransaction, withdrawAddress, withdrawAmount, selectedToken, balance, onPreSign, onWithdrawConfirmed, refetchBalance, refetchTokens]);

  const resetWithdraw = useCallback(() => {
    setWithdrawAddress('');
    setWithdrawAmount('');
    setWithdrawStatus('idle');
    setPendingSignature(null);
    setRetryCount(0);
    setNeedsAtaCreation(false);
  }, []);

  return {
    withdrawAddress,
    setWithdrawAddress,
    withdrawAmount,
    setWithdrawAmount,
    withdrawStatus,
    pendingSignature,
    needsAtaCreation,
    retryCount,
    handleMaxAmount,
    handleWithdraw,
    isWithdrawing,
    withdrawSymbol,
    resetWithdraw,
  };
}
